import type { Section } from '@common/formEngine/formEngine.types';
import type { NullablePrimitives, ValueOf } from '@common/utils/utilityTypes';
import { dependsOn } from '@common/formEngine/dependsOn';
import type { Locatie } from '../../neurology';
import { locatieOptions } from '../../neurology';
import { werkdiagnoseOptions } from '../lumbarRadiculopathy';

// Uitstralingspatroon
export const Uitstralingspatroon = {
    anders: 'Anders, namelijk...',
    geen_specifiek_patroon_diffuus: 'Geen specifiek patroon / diffuus',
    l1: 'L1',
    l2: 'L2',
    l3: 'L3',
    l4: 'L4',
    l5: 'L5',
} as const;
export type Uitstralingspatroon = ValueOf<typeof Uitstralingspatroon>;
export const uitstralingspatroonOptions = [
    Uitstralingspatroon.l1,
    Uitstralingspatroon.l2,
    Uitstralingspatroon.l3,
    Uitstralingspatroon.l4,
    Uitstralingspatroon.l5,
    Uitstralingspatroon.geen_specifiek_patroon_diffuus,
    Uitstralingspatroon.anders,
];

// Ja / Nee / Niet uitgevoerd
export const YesNoNotPerformed = {
    no: 'Nee',
    not_performed: 'Niet uitgevoerd',
    yes: 'Ja',
} as const;
export type YesNoNotPerformed = ValueOf<typeof YesNoNotPerformed>;
const yesNoNotPerformedOptions: YesNoNotPerformed[] = [
    YesNoNotPerformed.yes,
    YesNoNotPerformed.no,
    YesNoNotPerformed.not_performed,
];

export type LumbarRadiculopathyExaminationData = NullablePrimitives<{
    achillespeesreflex_afwijking: YesNoNotPerformed;
    achillespeesreflex_afwijking_locatie: Locatie;
    andere_bewegingen_of_houdingen_die_de_klachten_verlichten_of_verergeren: string;
    behandelvoorstel: string;
    intensiteit_van_de_pijn_enkele_weken_geleden_bij_bewegen: number;
    intensiteit_van_de_pijn_enkele_weken_geleden_in_rust: number;
    intensiteit_van_de_pijn_nu_bij_bewegen: number;
    intensiteit_van_de_pijn_nu_in_rust: number;
    kniepeesreflex_afwijking: YesNoNotPerformed;
    kniepeesreflex_afwijking_locatie: Locatie;
    mate_van_beperkingen_door_rugpijn_in_het_dagelijks_functioneren: string;
    overige_bevindingen: string;
    positieve_gekruiste_lasegue: YesNoNotPerformed;
    positieve_test_van_lasegue: YesNoNotPerformed;
    sensibiliteitsverlies_in_dermatomen: boolean;
    sensibiliteitsverlies_in_dermatomen_specificatie: string;
    uitstralende_pijn_in_been_bij_lopen_verdwijnt_bij_zitten_en_fietsen: boolean;
    uitstralende_pijn_is_erger_dan_rugpijn: boolean;
    uitstralende_pijn_verergert_bij_hoesten_niezen_of_persen: boolean;
    uitstralingspatroon: NullablePrimitives<{
        anders: string;
        geen_specifiek_patroon_diffuus: boolean;
        l1: boolean;
        l2: boolean;
        l3: boolean;
        l4: boolean;
        l5: boolean;
    }>;
    vraag_aan_neuroloog: string;
    werkdiagnose: string;
    zwakte_bij_op_tenen_lopen: YesNoNotPerformed;
    zwakte_bij_op_tenen_lopen_locatie: Locatie;
    zwakte_van_voetheffers: YesNoNotPerformed;
    zwakte_van_voetheffers_locatie: Locatie;
}>;

export const lumbarRadiculopathyExaminationSections: Section<LumbarRadiculopathyExaminationData>[] = [
    {
        fields: [
            {
                isRequired: true,
                key: 'uitstralende_pijn_is_erger_dan_rugpijn',
                label: 'Uitstralende pijn is erger dan rugpijn',
                type: 'boolean',
            },
            {
                isRequired: true,
                key: 'uitstralende_pijn_verergert_bij_hoesten_niezen_of_persen',
                label: 'Uitstralende pijn verergert bij hoesten, niezen of persen',
                type: 'boolean',
            },
            {
                checkboxEnumType: Uitstralingspatroon,
                isRequired: true,
                key: 'uitstralingspatroon',
                label: 'Uitstralingspatroon',
                type: 'checkboxGroup',
                valueOptions: uitstralingspatroonOptions,
            },
            {
                isRequired: true,
                key: 'sensibiliteitsverlies_in_dermatomen',
                label: 'Sensibiliteitsverlies in dermatomen',
                type: 'boolean',
            },
            {
                include: dependsOn('sensibiliteitsverlies_in_dermatomen'),
                isRequired: true,
                key: 'sensibiliteitsverlies_in_dermatomen_specificatie',
                label: 'Specificeer het dermatoom',
                type: 'textInput',
            },
        ],
        title: 'Neurologische symptomen',
    },
    {
        fields: [],
        subsections: [
            {
                fields: [
                    {
                        isRequired: true,
                        key: 'intensiteit_van_de_pijn_nu_in_rust',
                        label: 'In rust',
                        maxValue: 10,
                        minValue: 0,
                        type: 'number',
                    },
                    {
                        isRequired: true,
                        key: 'intensiteit_van_de_pijn_nu_bij_bewegen',
                        label: 'Bij bewegen',
                        maxValue: 10,
                        minValue: 0,
                        type: 'number',
                    },
                ],
                title: 'Intensiteit van de pijn nu, op schaal van 0 tot 10',
            },
            {
                fields: [
                    {
                        isRequired: true,
                        key: 'intensiteit_van_de_pijn_enkele_weken_geleden_in_rust',
                        label: 'In rust',
                        maxValue: 10,
                        minValue: 0,
                        type: 'number',
                    },
                    {
                        isRequired: true,
                        key: 'intensiteit_van_de_pijn_enkele_weken_geleden_bij_bewegen',
                        label: 'Bij bewegen',
                        maxValue: 10,
                        minValue: 0,
                        type: 'number',
                    },
                ],
                title: 'Pijn enkele weken geleden, op schaal van 0 tot 10',
            },
        ],
        title: 'Pijnkarakteristieken',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'mate_van_beperkingen_door_rugpijn_in_het_dagelijks_functioneren',
                label: 'Mate van beperkingen door rugpijn in het dagelijks functioneren',
                type: 'textArea',
            },
            {
                isRequired: true,
                key: 'uitstralende_pijn_in_been_bij_lopen_verdwijnt_bij_zitten_en_fietsen',
                label: 'Uitstralende pijn in het been bij het lopen verdwijnt bij zitten en fietsen',
                type: 'boolean',
            },
            {
                isRequired: true,
                key: 'andere_bewegingen_of_houdingen_die_de_klachten_verlichten_of_verergeren',
                label: 'Andere bewegingen of houdingen die de klachten verlichten of verergeren',
                type: 'textArea',
            },
        ],
        title: 'Functionele beperkingen',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'positieve_test_van_lasegue',
                label: 'Positieve test van Lasègue',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                isRequired: true,
                key: 'positieve_gekruiste_lasegue',
                label: 'Positieve gekruiste Lasègue',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                isRequired: true,
                key: 'achillespeesreflex_afwijking',
                label: 'Achillespeesreflex afwijking',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                include: dependsOn(
                    'achillespeesreflex_afwijking',
                    ({ achillespeesreflex_afwijking }) => achillespeesreflex_afwijking === YesNoNotPerformed.yes
                ),
                isRequired: true,
                key: 'achillespeesreflex_afwijking_locatie',
                label: 'Locatie',
                type: 'radioGroup',
                valueOptions: locatieOptions,
            },
            {
                isRequired: true,
                key: 'kniepeesreflex_afwijking',
                label: 'Kniepeesreflex afwijking',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                include: dependsOn(
                    'kniepeesreflex_afwijking',
                    ({ kniepeesreflex_afwijking }) => kniepeesreflex_afwijking === YesNoNotPerformed.yes
                ),
                isRequired: true,
                key: 'kniepeesreflex_afwijking_locatie',
                label: 'Locatie',
                type: 'radioGroup',
                valueOptions: locatieOptions,
            },
            {
                isRequired: true,
                key: 'zwakte_van_voetheffers',
                label: 'Zwakte van voetheffers',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                include: dependsOn(
                    'zwakte_van_voetheffers',
                    ({ zwakte_van_voetheffers }) => zwakte_van_voetheffers === YesNoNotPerformed.yes
                ),
                isRequired: true,
                key: 'zwakte_van_voetheffers_locatie',
                label: 'Locatie',
                type: 'radioGroup',
                valueOptions: locatieOptions,
            },
            {
                isRequired: true,
                key: 'zwakte_bij_op_tenen_lopen',
                label: 'Zwakte bij op tenen lopen',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotPerformedOptions,
            },
            {
                include: dependsOn(
                    'zwakte_bij_op_tenen_lopen',
                    ({ zwakte_bij_op_tenen_lopen }) => zwakte_bij_op_tenen_lopen === YesNoNotPerformed.yes
                ),
                isRequired: true,
                key: 'zwakte_bij_op_tenen_lopen_locatie',
                label: 'Locatie',
                type: 'radioGroup',
                valueOptions: locatieOptions,
            },
        ],
        title: 'Lichamelijk onderzoek',
    },
    {
        fields: [
            {
                key: 'overige_bevindingen',
                label: 'Overige bevindingen',
                type: 'textArea',
            },
            {
                allowOtherOption: true,
                isRequired: true,
                key: 'werkdiagnose',
                label: 'Werkdiagnose',
                type: 'radioGroup',
                valueOptions: werkdiagnoseOptions,
            },
        ],
        title: 'Conclusie',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'behandelvoorstel',
                label: 'Behandelvoorstel',
                type: 'textArea',
            },
        ],
        title: 'Advies en behandelbeleid',
    },
    {
        fields: [
            {
                key: 'vraag_aan_neuroloog',
                label: 'Vraag aan neuroloog',
                type: 'textArea',
            },
        ],
        title: 'Overige vragen',
    },
];
